import { useEffect, useState } from "react";
import { useBlocker } from "react-router";
import { useIntl } from "react-intl";
import { usePendingEdits } from "@/lib/pending-edits";
import { localizedMessageOf } from "@/lib/localized-error";
import { logApplicationFailure } from "@/lib/user-error";
import { UnsavedChangesDialog } from "@/components/common/UnsavedChangesDialog";

// Protects against data loss: warns about unsaved inline edits before the
// user navigates (in-app) or closes/reloads the tab. Dirty state comes from
// the central PendingEdits store.
//
// This one owns the NAVIGATION occasion and nothing else. An action that destroys edits without
// moving the URL — changing the acting-company context, which empties the Apollo cache and remounts
// the editors under it — never reaches a router blocker, so it holds itself through
// lib/unsaved-edits-gate.ts and renders the same dialog.

export function UnsavedGuard() {
  const intl = useIntl();
  const ctx = usePendingEdits();
  const dirtyScopes = Object.values(ctx?.scopes ?? {}).filter((s) => s.dirty);
  const hasDirty = dirtyScopes.length > 0;
  // A scope with nothing to save on the way out (`leaveOnly`) is only kept or left; once every dirty scope is like that,
  // the dialog asks only stay or leave (spark#1075).
  const canSave = dirtyScopes.some((s) => !s.leaveOnly);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Tab close / reload → native browser warning.
  useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasDirty]);

  // In-app navigation blocked while dirty. Asked at call time: the router registers this predicate
  // from an effect, so the rendered `hasDirty` would be a commit old when a screen clears its scope
  // in the registry and navigates in the same handler — and the dialog would open over a save that
  // worked (spark#1213).
  const hasDirtyScope = ctx?.hasDirtyScope;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      Boolean(hasDirtyScope?.()) && currentLocation.pathname !== nextLocation.pathname,
  );

  // A BLOCKER THAT FIRED IS NOT A BLOCKER THAT STILL APPLIES. The registry itself hears of a scope
  // from usePendingScope's effect, so a page that clears only its own state and then navigates is
  // still stopped by a dirty state that no longer exists. Nothing would be lost by proceeding and
  // there is nothing to ask about, so the navigation is let through rather than answered with a
  // dialog listing no edits.
  //
  // This can never release a navigation that ought to be held: it only fires when NO scope is
  // dirty, and a held navigation with nothing dirty is the dialog asking about nothing.
  useEffect(() => {
    if (blocker.state === "blocked" && !hasDirty) blocker.proceed?.();
  }, [blocker, hasDirty]);

  // Published for screens that navigate after their own write (pending-edits.tsx, isLeaveHeld).
  const setLeaveHeld = ctx?.setLeaveHeld;
  useEffect(() => {
    setLeaveHeld?.(blocker.state === "blocked");
    return () => setLeaveHeld?.(false);
  }, [blocker.state, setLeaveHeld]);

  const onStay = () => {
    setError("");
    blocker.reset?.();
  };

  const onDiscard = () => {
    setError("");
    dirtyScopes.forEach((s) => s.discard());
    blocker.proceed?.();
  };

  const onSave = async () => {
    setSaving(true);
    setError("");
    try {
      await Promise.all(dirtyScopes.filter((s) => !s.leaveOnly).map((s) => s.save()));
      ctx?.signalSaved();
      blocker.proceed?.();
    } catch (e) {
      // Only a LocalizedError's message reaches the screen — same rule and same reasoning as
      // unsaved-edits-gate.ts, which this path predates.
      const localized = localizedMessageOf(e);
      if (!localized) logApplicationFailure("unsaved-guard save");
      setError(
        localized ?? intl.formatMessage({ id: "common.saveFailed", defaultMessage: "Save failed." }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <UnsavedChangesDialog
      id="unsaved-guard"
      open={blocker.state === "blocked"}
      // A save already running (the SaveBar's) keeps the buttons closed; once nothing is left unsaved,
      // the effect above lets the navigation through — saving again here would repeat its mutations.
      saving={saving || Boolean(ctx?.saving)}
      error={error}
      onStay={onStay}
      onDiscard={onDiscard}
      onSave={canSave ? () => void onSave() : undefined}
    />
  );
}
